// Modules
import { useState, useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";
import moment from "moment";
import { useLocation } from "react-router-dom";

// Styles
import styles from "containers/MonthlyDeclaration/MonthlyDeclarationPage.module.css";

// Components
import MonthlyDeclarationButtons, { ButtonVariants } from "containers/MonthlyDeclaration/MonthlyDeclarationButtons";
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// Constants
import { MONTHLY_DECLARATIONS as Constants, REIM_DECLARATION_CONFIG_CODES, ReimbursementSummaryStatus as StatusConstants, StatusInfo } from "constants/global";

// Type Definitions
import type { GroupDetails } from "containers/ReimbursementVerification/useReimbursementVerification";
import type { ReimbSummaryDataType } from "redux/types";
import type { ConfigDataItem } from "services/api/types";
import type { HeaderStatusInfoProps } from "components/molecules/HeaderStatusInfo/HeaderStatusInfo";

// Utils
import getIcon from "utils/getIcon";
import { downloadFile } from "utils/downloadFile";

// Redux
import {
	getReimbSummary,
	reimbSummaryListSelector,
	getReimbVoucherGroupList,
	reimbVoucherGroupListSelector,
	selectedVoucherGroupCode,
	getLoadingStateForReimbSummaryList,
	getVoucherDetailsReport,
	getLoadingSelector
} from "redux/reimbursement/reimbursementSlice";
import { getConfigData, getAdminEmail, getConfigSelector, selectedOutletCodeSelector, showHideFilter, toggleFilter } from "redux/userList/userSlice";

type FilterDataType = { [key: string]: boolean };

export const useMonthlyDeclaration = () => {
	const dispatch = useAppDispatch();
	const reimbSummaryList: any = useAppSelector(reimbSummaryListSelector);
	const outletCode = useAppSelector(selectedOutletCodeSelector);
	const voucherGroupList = useAppSelector(reimbVoucherGroupListSelector);
	const selectedVoucherGroupCodeDetails = useAppSelector(selectedVoucherGroupCode);
	const configDetails = useAppSelector(getConfigSelector);
	const { data: configDetailsData } = configDetails || {};
	const dataLoading = useAppSelector(getLoadingSelector);
	const summaryDataLoading = useAppSelector(getLoadingStateForReimbSummaryList);
	const showFilter = useAppSelector(showHideFilter);
	const { state, pathname } = useLocation();

	const [isSelectionAvailable, setIsSelectionAvailable] = useState<boolean>(false);

	const threeMonthInfo = [
		moment(moment().subtract(1, "months").calendar()).format("YYYYMM"),
		moment(moment().subtract(2, "months").calendar()).format("YYYYMM"),
		moment(moment().subtract(6, "months").calendar()).format("YYYYMM")
	];

	/* This array has the values of all reimbursement status from the summary list*/
	/* -1 is the key when voucher group code is not passed in the API payload*/
	const currentMonthReimbStatusArray = reimbSummaryList?.[-1]?.reimb_summ.map((item: ReimbSummaryDataType) => {
		return item.reimb_status;
	});

	useEffect(() => {
		dispatch(getConfigData({ config_mst_code: "7000" }));
		dispatch(getAdminEmail());
	}, []);

	const isInDateRange = useMemo(() => {
		const currentDate = new Date().getDate();
		const startDate = configDetailsData?.find((_config: ConfigDataItem) => _config?.config_dtl_code === REIM_DECLARATION_CONFIG_CODES.START_DATE)?.config_dtl_val3;
		const endDate = configDetailsData?.find((_config: ConfigDataItem) => _config?.config_dtl_code === REIM_DECLARATION_CONFIG_CODES.END_DATE)?.config_dtl_val3;
		if (currentDate >= Number(startDate) && currentDate <= Number(endDate)) {
			return true;
		}
		return false;
	}, [configDetails, configDetailsData]);

	useEffect(() => {
		if (selectedVoucherGroupCodeDetails) {
			Object.keys(selectedVoucherGroupCodeDetails).length === 0 ? setIsSelectionAvailable(false) : setIsSelectionAvailable(true);
		}
	}, [selectedVoucherGroupCodeDetails]);

	const apiPayload = useMemo(() => {
		return {
			from_year_month: threeMonthInfo[2],
			to_year_month: threeMonthInfo[0],
			outlet_code: outletCode
		};
	}, [outletCode]);

	const popOverClose = useCallback(
		(record: any) => {
			const { summary_item } = record;
			const filterDataInfo: FilterDataType = {};
			if (showFilter[summary_item?.reimb_summ_id]) {
				filterDataInfo[summary_item?.reimb_summ_id] = false;
				dispatch(toggleFilter(filterDataInfo));
			}
		},
		[showFilter, dispatch, toggleFilter]
	);

	const generateCols = () => {
		return [
			{
				dataIndex: "year_month",
				title: <p className={styles?.headerContentText}>{Constants.ACCORDION_HEADER_DESC.MONTH}</p>,
				width: 150
			},
			{
				dataIndex: "net_amount",
				title: <p className={styles?.headerContentText}>{Constants.ACCORDION_HEADER_DESC.AMOUNT}</p>,
				width: 170
			},
			{
				dataIndex: "tenant_amount",
				title: <p className={styles?.headerContentText}>{Constants.ACCORDION_HEADER_DESC.DECLARED}</p>,
				width: 170
			},
			{
				dataIndex: "reim_amount",
				title: <p className={styles?.headerContentText}>{Constants.ACCORDION_HEADER_DESC.REIMBURSEMENT}</p>,
				width: 170
			},
			{
				dataIndex: "reimb_status",
				title: <p className={styles?.headerContentText}>{Constants.ACCORDION_HEADER_DESC.STATUS}</p>,
				width: 275
			},
			{
				dataIndex: "download",
				title: <p className={styles?.headerContentText}>{Constants.ACCORDION_HEADER_DESC.DOWNLOAD}</p>,
				width: 125
			},
			{
				dataIndex: "buttons",
				title: ""
			}
		];
	};

	const generateButtonVariant = (itemStatus: string, itemTenantRejected: string): ButtonVariants => {
		if (isInDateRange) {
			if (itemStatus === StatusConstants.PENDING) {
				return Constants.DECLARE_VARIANT as ButtonVariants;
			}
			if (itemStatus === StatusConstants.ACCEPTED || itemStatus === StatusConstants.PENDING_VERIFICATION) {
				return Constants.VIEW_EDIT_VARIANT as ButtonVariants;
			}
		} else {
			if (itemTenantRejected === "X") {
				return Constants.VIEW_VARIANT as ButtonVariants;
			}
		}
		return "";
	};

	const isVoucherGroupDataAvailable = useMemo(() => {
		return !!voucherGroupList?.groupList[pathname]?.length;
	}, [voucherGroupList, pathname]);

	const statusBarInfo = useMemo((): HeaderStatusInfoProps => {
		const currentDate = new Date().getDate();
		const processPostDate = configDetailsData?.find((_config: ConfigDataItem) => _config?.config_dtl_code === REIM_DECLARATION_CONFIG_CODES.PROCESS_POST_DATE)?.config_dtl_val3;
		const endDate = configDetailsData?.find((_config: ConfigDataItem) => _config?.config_dtl_code === REIM_DECLARATION_CONFIG_CODES.END_DATE)?.config_dtl_val3;

		if (!isVoucherGroupDataAvailable) {
			return {
				message: <p>{StatusInfo.NO_ACTION_REQ}</p>,
				background: "green"
			};
		}

		if (currentDate <= Number(endDate)) {
			if (currentMonthReimbStatusArray?.find((currentStatus: string) => currentStatus.toUpperCase() === StatusConstants.PENDING)) {
				return {
					message: <p>{`You are required to make your declaration by ${endDate}th of the month`}</p>,
					background: "red"
				};
			} else {
				return {
					message: <p>{`Submission may be edited before the ${endDate}th of every month and final report will be generated on the ${processPostDate}th`}</p>,
					background: "yellow"
				};
			}
		} else if (currentDate > Number(endDate) && currentDate < Number(processPostDate)) {
			if (currentMonthReimbStatusArray?.find((currentStatus: string) => currentStatus.toUpperCase() === StatusConstants.PENDING)) {
				return {
					message: <p>{`You are unable to submit your declaration due to the cut off date (${endDate}th of each month). `}</p>,
					background: "red"
				};
			} else if (
				currentMonthReimbStatusArray?.find((currentStatus: string) => currentStatus.toUpperCase() === StatusConstants.PENDING_VERIFICATION) ||
				currentMonthReimbStatusArray?.find((currentStatus: string) => currentStatus.toUpperCase() === StatusConstants.PENDING_APPROVAL)
			) {
				return {
					message: <p>{`Currently processing your claim. The final report will be generated on the ${processPostDate}th and you will be notified via email`}</p>,
					background: "yellow"
				};
			} else {
				return {
					message: <p>{StatusInfo.NO_ACTION_REQ}</p>,
					background: "green"
				};
			}
		}

		return {
			message: <p>{StatusInfo.NO_ACTION_REQ}</p>,
			background: "green"
		};
	}, [generateButtonVariant, configDetailsData, isVoucherGroupDataAvailable, reimbSummaryList, outletCode, currentMonthReimbStatusArray]);

	const onFailure = useCallback(() => {
		CountDownModal({
			timer: 3,
			title: Constants.REPORT_NOT_GENERATED,
			errorIcon: true
		});
	}, [CountDownModal]);

	const handleDownload = useCallback(
		async (month: string, voucherGroup: string) => {
			try {
				const payload = { from_year_month: month, to_year_month: month, outlet_code: outletCode, voucher_group_code: voucherGroup };

				const { data, errors } = await dispatch(getVoucherDetailsReport(payload)).unwrap();

				if (data?.file_url && !errors?.length) {
					try {
						await downloadFile(data?.file_url);

						CountDownModal({
							timer: 3,
							title: Constants.REPORT_GENERATED
						});
					} catch (error) {
						onFailure();
					}
				} else {
					onFailure();
				}
			} catch (error) {
				onFailure();
			}
		},
		[onFailure, downloadFile, CountDownModal, dispatch, outletCode, getVoucherDetailsReport]
	);

	const generateTableData = useCallback(() => {
		const dataObject: {
			[key: string]: Array<ReimbSummaryDataType>;
		} = {};

		if (voucherGroupList) {
			voucherGroupList?.groupList[pathname]?.forEach((item: GroupDetails) => {
				const voucherGroupCode = item?.voucher_group_code.toString();

				if (reimbSummaryList) {
					const summaryInfo = reimbSummaryList[-1]?.reimb_summ
						.filter((summaryInfoItem: any) => summaryInfoItem.voucher_group_code === voucherGroupCode)
						.map((summaryDetailItem: ReimbSummaryDataType) => {
							const variant: ButtonVariants = generateButtonVariant(summaryDetailItem.reimb_status.toUpperCase(), summaryDetailItem.tenant_rejected);
							const { net_amount, tenant_amount, reimb_amount } = summaryDetailItem;
							return {
								year_month: <p className={styles?.extraContentText}>{moment(summaryDetailItem.yearmonth).format("MMM")}</p>,
								net_amount: <>{!!net_amount && <p className={styles?.extraContentText}>{`S$${net_amount}`}</p>}</>,
								tenant_amount: <>{!!tenant_amount && <p className={styles?.extraContentText}>{`S$${tenant_amount}`}</p>}</>,
								reim_amount: <>{!!reimb_amount && <p className={styles?.extraContentTextGreen}>{`S$${reimb_amount}`}</p>}</>,
								reimb_status: <p className={styles?.extraContentText}>{summaryDetailItem.reimb_status.toUpperCase()?.replace("_", " ")}</p>,
								download: <span>{getIcon({ icon: "download", onClickHandler: () => handleDownload(summaryDetailItem.yearmonth, voucherGroupCode) })}</span>,
								buttons: <MonthlyDeclarationButtons reimbSummaryItem={summaryDetailItem} variant={variant} />,
								summary_item: summaryDetailItem,
								key: summaryDetailItem.reimb_summ_id
							};
						});

					dataObject[voucherGroupCode] = summaryInfo?.sort((obj1: any, obj2: any) => parseInt(obj2.summary_item?.yearmonth) - parseInt(obj1.summary_item?.yearmonth));
				}
			});
		}

		return dataObject;
	}, [voucherGroupList, reimbSummaryList, outletCode, configDetails, state, handleDownload]);

	const generateTableDisplayData = (groupCode: string) => {
		const data = generateTableData();
		return data[groupCode];
	};

	useEffect(() => {
		if (outletCode || state?.reload) {
			dispatch(getReimbVoucherGroupList({ reqPayload: apiPayload, pathName: pathname }));
			dispatch(getReimbSummary(apiPayload));
		}
	}, [apiPayload, state]);

	return {
		voucherGroupList,
		isSelectionAvailable,
		selectedVoucherGroupCodeDetails,
		pathname,
		statusBarInfo,
		loading: dataLoading === 1 || summaryDataLoading === 1,
		isVoucherGroupDataAvailable,
		popOverClose,
		generateCols,
		generateTableDisplayData
	};
};
